/**
 * useLearnerContext — Gedeeld leerling-aggregaat voor het mascot-domein.
 *
 * DDD: voorheen werd deze berekening op TWEE plekken gedupliceerd:
 *   - FloatingDjay.tsx (voor proactieve messages)
 *   - useVariantUnlocks.ts (voor variant unlocks)
 * Beide itereerden MODULES, telden answered-questions en mastery-counts.
 *
 * Eén bron van waarheid voorkomt dat de mastery-drempel op twee plekken
 * gewijzigd moet worden, en levert dezelfde memoization-key voor beide
 * consumers.
 *
 * Audit-referentie: docs/audits/MASCOT_DOMAIN_AUDIT_2026.md §2.2 DDD-3.
 */

import { useMemo } from 'react'
import { useAppStore } from '../app/hooks/useAppStore'
import { MODULES } from '../content'
import { DJAY_THRESHOLDS } from './config'
import type { LearnerContext } from './djayMessages'

/**
 * Volledige leerling-context inclusief afgeleide aggregaten.
 * Wordt gememoized op alle relevante store-velden.
 */
export function useLearnerContext(): LearnerContext {
  const {
    state,
    activeAccount,
    getModuleMasteryForGroup,
    getAnsweredQuestions,
  } = useAppStore()

  const displayName = activeAccount?.displayName || 'vriend'
  const streak = state?.streak?.currentTotalStreak || 0
  const level = state?.stats?.level || 1
  const xp = state?.stats?.xp || 0

  return useMemo<LearnerContext>(() => {
    let masteredCount = 0
    let totalQuestionsAnswered = 0
    MODULES.forEach((m) => {
      totalQuestionsAnswered += getAnsweredQuestions(m.id).length
      if (
        getModuleMasteryForGroup(m.id) >= DJAY_THRESHOLDS.MODULE_MASTERY_PCT
      ) {
        masteredCount += 1
      }
    })
    return {
      displayName,
      streak,
      level,
      xp,
      masteredCount,
      totalQuestionsAnswered,
      dayOfWeek: new Date().getDay(),
    }
  }, [
    displayName,
    streak,
    level,
    xp,
    getModuleMasteryForGroup,
    getAnsweredQuestions,
  ])
}

/**
 * Lichtere versie voor consumers die alleen de basis-stats nodig hebben
 * (geen mastery/answered iteratie). Voorkomt onnodige module-loops.
 */
export function useLearnerBasics(): Pick<
  LearnerContext,
  'displayName' | 'streak' | 'level' | 'xp' | 'dayOfWeek'
> {
  const { state, activeAccount } = useAppStore()
  const displayName = activeAccount?.displayName || 'vriend'
  const streak = state?.streak?.currentTotalStreak || 0
  const level = state?.stats?.level || 1
  const xp = state?.stats?.xp || 0

  return useMemo(
    () => ({
      displayName,
      streak,
      level,
      xp,
      dayOfWeek: new Date().getDay(),
    }),
    [displayName, streak, level, xp],
  )
}
